import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { apiRequest } from "@/lib/queryClient";

type TimeRange = "30days" | "90days" | "6months" | "1year";

interface UserAdoptionChartProps {
  timeRangeFilter?: string;
}

// Map between filter values and chart time ranges
const timeRangeMapping: Record<string, TimeRange> = {
  "7days": "30days", // Default to 30 days if 7 days not available
  "30days": "30days",
  "90days": "90days",
  "1year": "1year"
};

export default function UserAdoptionChart({ timeRangeFilter }: UserAdoptionChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("30days");

  // Update time range if the filter changes
  useEffect(() => {
    if (timeRangeFilter && timeRangeMapping[timeRangeFilter]) {
      setTimeRange(timeRangeMapping[timeRangeFilter]);
    }
  }, [timeRangeFilter]);
  
  // Fetch user adoption data from the API with proper tenant context
  const { data: userAdoptionData, isLoading, error } = useQuery({
    queryKey: ['/api/analytics/user-adoption', timeRange],
    queryFn: async () => {
      return await apiRequest("GET", `/api/analytics/user-adoption?timeRange=${timeRange}`);
    }
  });

  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-slate-200 shadow-sm rounded-md">
          <p className="font-medium text-slate-800">{label}</p>
          <p className="text-sm mt-1 text-primary">Assigned Seats: {payload[0]?.value || 0}</p>
          <p className="text-sm mt-1 text-slate-400">Total Seats: {payload[1]?.value || 0}</p>
          <p className="text-sm mt-1 text-green-600">New Employees: {payload[2]?.value || 0}</p>
          <p className="text-sm mt-1 font-medium">
            Utilization Rate: {payload[0]?.value && payload[1]?.value 
              ? Math.round((payload[0].value / payload[1].value) * 100) 
              : 0}%
          </p>
        </div>
      );
    }
    return null;
  };
  
  // Display a loading state when data is being fetched
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-64" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <div className="h-80 w-full mt-4">
            <Skeleton className="h-full w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Display an error message if the fetch failed
  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load user adoption data. This could be due to a connection issue or server problem.
              Please try refreshing the page.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Check if we have data
  if (!userAdoptionData || userAdoptionData.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center space-x-2">
            <CardTitle className="text-lg font-semibold">License Seat Utilization Trends</CardTitle>
          </div>
          <Select defaultValue={timeRange} onValueChange={(value) => setTimeRange(value as TimeRange)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
              <SelectItem value="6months">Last 6 months</SelectItem>
              <SelectItem value="1year">Last year</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="py-12 text-center">
            <p className="text-muted-foreground">No user adoption data available for the selected time range.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const lastDataPoint = userAdoptionData[userAdoptionData.length - 1];
  const firstDataPoint = userAdoptionData[0];
  const totalNewUsers = userAdoptionData.reduce((sum: number, item: { newUsers: number }) => sum + item.newUsers, 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-2">
          <CardTitle className="text-lg font-semibold">License Seat Utilization Trends</CardTitle>
          <Popover>
            <PopoverTrigger>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <InfoCircledIcon className="h-4 w-4 text-slate-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-medium text-sm">About This Chart</h4>
                <p className="text-sm text-muted-foreground">
                  This chart shows the growth of license seat allocation over time. It tracks assigned seats,
                  total purchased seats, and newly added employees during the selected period.
                </p>
                <h4 className="font-medium text-sm mt-4">How It's Calculated</h4>
                <p className="text-sm text-muted-foreground">
                  <strong>Assigned Seats:</strong> License seats that have been assigned to employees<br />
                  <strong>Total Seats:</strong> Total number of purchased license seats<br />
                  <strong>New Employees:</strong> Employees added during the time period<br />
                  <strong>Utilization Rate:</strong> Percentage of purchased seats assigned to employees
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <Select defaultValue={timeRange} onValueChange={(value) => setTimeRange(value as TimeRange)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30days">Last 30 days</SelectItem>
            <SelectItem value="90days">Last 90 days</SelectItem>
            <SelectItem value="6months">Last 6 months</SelectItem>
            <SelectItem value="1year">Last year</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-600 font-medium">Assigned Seats</p>
            <p className="text-2xl font-bold mt-1">
              {lastDataPoint.active}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              +{lastDataPoint.active - firstDataPoint.active} from period start
            </p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="text-sm text-slate-600 font-medium">Total Seats</p>
            <p className="text-2xl font-bold mt-1">
              {lastDataPoint.total}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              +{lastDataPoint.total - firstDataPoint.total} from period start
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-600 font-medium">New Employees</p>
            <p className="text-2xl font-bold mt-1">
              {totalNewUsers}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              During selected period
            </p>
          </div>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={userAdoptionData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={customTooltip} />
              <Area 
                type="monotone" 
                dataKey="active" 
                stackId="1" 
                stroke="#2563eb" 
                fill="#2563eb" 
                fillOpacity={0.6}
              />
              <Area 
                type="monotone" 
                dataKey="total" 
                stackId="2" 
                stroke="#94a3b8" 
                fill="#94a3b8" 
                fillOpacity={0.2}
              />
              <Area 
                type="monotone" 
                dataKey="newUsers" 
                stackId="3" 
                stroke="#10b981" 
                fill="#10b981" 
                fillOpacity={0.4}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
